/**
 * useJoinENDemoWeekViewLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait JoinENDemoWeekView
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, JoinENDemoWeekStep, JoinENDemoWeekViewEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type JoinENDemoWeekViewState =
  | 'loading'
  | 'running'
  | 'completed'
  | 'cancelled'
  | 'error';

export type JoinENDemoWeekViewEvent =
  | 'INIT'
  | 'WizardLoaded'
  | 'WizardLoadFailed'
  | 'WizardSaved'
  | 'WizardSaveFailed'
  | 'ADVANCE'
  | 'RETREAT'
  | 'CANCEL'
  | 'RESTART'
  | 'TOUR_STEP'
  | 'TOUR_RESET';

type JoinENDemoWeekViewSlot = 'main' | 'toast';
interface JoinENDemoWeekViewInternalState {
  machineState: JoinENDemoWeekViewState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: JoinENDemoWeekViewEvent | null;
  slotWriter: Partial<Record<JoinENDemoWeekViewSlot, string | null>>;
  loading: boolean;
  pendingEvent: JoinENDemoWeekViewEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<JoinENDemoWeekStep>;
}

type JoinENDemoWeekViewAction =
  | { type: 'EVENT_SUCCESS'; event: JoinENDemoWeekViewEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: JoinENDemoWeekViewSlot[]; renderClear?: JoinENDemoWeekViewSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: JoinENDemoWeekViewEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof JoinENDemoWeekStep & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function JoinENDemoWeekViewRenderWriter(event: string, newState: string): string {
  return ['ADVANCE', 'WizardSaved'].includes(event) ? `${event}>${newState}` : event;
}

function joinENDemoWeekViewReducer(
  state: JoinENDemoWeekViewInternalState,
  action: JoinENDemoWeekViewAction
): JoinENDemoWeekViewInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as JoinENDemoWeekViewState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, JoinENDemoWeekViewRenderWriter(action.event, action.newState)] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<JoinENDemoWeekStep>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<JoinENDemoWeekStep>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<JoinENDemoWeekStep>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: JoinENDemoWeekViewInternalState = {
  machineState: 'loading',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface JoinENDemoWeekViewConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  backLabel?: string;
  cancelLabel?: string;
  cancelledText?: string;
  cancelledTitle?: string;
  cardLook: 'elevated' | 'flat-bordered' | 'borderless-divider' | 'ticket' | 'invoice' | 'chip' | 'tile-image-first';
  completedText?: string;
  completedTitle?: string;
  nextLabel?: string;
  restartLabel?: string;
  stepDescriptions?: string[];
  stepLabels?: string[];
  steps: { description: string; fields: FieldValue[]; icon: string; key: string; label: string }[];
  submitLabel?: string;
  title?: string;
}

const DEFAULT_JOIN_EN_DEMO_WEEK_VIEW_CONFIG = {
  cardLook: "chip",
  steps: [{ description: "", fields: [], icon: "users", key: "team", label: "Meet the team" }, { description: "", fields: [], icon: "hammer", key: "build", label: "Build your first system" }, { description: "", fields: [], icon: "rocket", key: "ship", label: "Ship it" }],
} as JoinENDemoWeekViewConfig;

export interface JoinENDemoWeekViewLogicReturn {
  state: JoinENDemoWeekViewState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: JoinENDemoWeekViewEvent | null;
  slotWriter: Partial<Record<JoinENDemoWeekViewSlot, string | null>>;
  loading: boolean;
  pendingEvent: JoinENDemoWeekViewEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<JoinENDemoWeekStep>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof JoinENDemoWeekViewEventPayloadMap]: (payload?: JoinENDemoWeekViewEventPayloadMap[K]) => Promise<EventResponse> };
  config: JoinENDemoWeekViewConfig;
}

const __CASCADE_EVENTS = new Set<string>(['ADVANCE', 'RESTART', 'TOUR_RESET', 'TOUR_STEP']);

const __LEG_EVENTS: ReadonlySet<string> = new Set(['ADVANCE', 'INIT', 'RESTART', 'WizardSaved']);

export function useJoinENDemoWeekViewLogic(config?: Partial<JoinENDemoWeekViewConfig>): JoinENDemoWeekViewLogicReturn {
  const mergedConfig: JoinENDemoWeekViewConfig = { ...DEFAULT_JOIN_EN_DEMO_WEEK_VIEW_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/join-en-demo-week-view', orbitalName: 'JoinEN', traitName: 'JoinENDemoWeekView' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(joinENDemoWeekViewReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['JoinENDemoWeekStep'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'loading' && event === 'INIT') {
      // loading → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'WizardLoaded') {
      // loading → running (on WizardLoaded)
        _fields['currentStepIndex'] = 0;
        fields = { ...(fields ?? {}), 'currentStepIndex': _fields['currentStepIndex'] } as Record<string, EventPayloadValue | undefined>;
        _fields['totalSteps'] = ([{ key: "team" as const, label: "Meet the team" as const, description: "" as const, icon: "users" as const, fields: [] }, { icon: "hammer" as const, description: "" as const, key: "build" as const, label: "Build your first system" as const, fields: [] }, { label: "Ship it" as const, key: "ship" as const, description: "" as const, fields: [], icon: "rocket" as const }]).length;
        fields = { ...(fields ?? {}), 'totalSteps': _fields['totalSteps'] } as Record<string, EventPayloadValue | undefined>;
        _fields['wizardSteps'] = ([{ key: "team" as const, description: "" as const, fields: [], label: "Meet the team" as const, icon: "users" as const }, { icon: "hammer" as const, description: "" as const, key: "build" as const, label: "Build your first system" as const, fields: [] }, { label: "Ship it" as const, key: "ship" as const, icon: "rocket" as const, fields: [], description: "" as const }]).map((step) => ({ title: getNestedValue(step, "label"), id: getNestedValue(step, "key"), description: (getNestedValue(step, "description") ?? "") }));
        fields = { ...(fields ?? {}), 'wizardSteps': _fields['wizardSteps'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentStepLabel'] = getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ icon: "users" as const, description: "" as const, label: "Meet the team" as const, key: "team" as const, fields: [] }, { label: "Build your first system" as const, description: "" as const, icon: "hammer" as const, fields: [], key: "build" as const }, { label: "Ship it" as const, key: "ship" as const, description: "" as const, icon: "rocket" as const, fields: [] }], (0)), "label");
        fields = { ...(fields ?? {}), 'currentStepLabel': _fields['currentStepLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentStepDescription'] = (getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ label: "Meet the team" as const, description: "" as const, fields: [], icon: "users" as const, key: "team" as const }, { icon: "hammer" as const, description: "" as const, fields: [], label: "Build your first system" as const, key: "build" as const }, { key: "ship" as const, label: "Ship it" as const, fields: [], description: "" as const, icon: "rocket" as const }], (0)), "description") ?? "");
        fields = { ...(fields ?? {}), 'currentStepDescription': _fields['currentStepDescription'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentStepIcon'] = (getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ description: "" as const, fields: [], icon: "users" as const, key: "team" as const, label: "Meet the team" as const }, { key: "build" as const, label: "Build your first system" as const, fields: [], icon: "hammer" as const, description: "" as const }, { key: "ship" as const, fields: [], description: "" as const, label: "Ship it" as const, icon: "rocket" as const }], (0)), "icon") ?? "circle");
        fields = { ...(fields ?? {}), 'currentStepIcon': _fields['currentStepIcon'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentFields'] = (getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ fields: [], icon: "users" as const, key: "team" as const, description: "" as const, label: "Meet the team" as const }, { fields: [], description: "" as const, icon: "hammer" as const, label: "Build your first system" as const, key: "build" as const }, { key: "ship" as const, label: "Ship it" as const, icon: "rocket" as const, description: "" as const, fields: [] }], (0)), "fields") ?? []);
        fields = { ...(fields ?? {}), 'currentFields': _fields['currentFields'] } as Record<string, EventPayloadValue | undefined>;
        _fields['isFirstStep'] = true;
        fields = { ...(fields ?? {}), 'isFirstStep': _fields['isFirstStep'] } as Record<string, EventPayloadValue | undefined>;
        _fields['isLastStep'] = (([{ description: "" as const, icon: "users" as const, fields: [], label: "Meet the team" as const, key: "team" as const }, { key: "build" as const, label: "Build your first system" as const, icon: "hammer" as const, fields: [], description: "" as const }, { key: "ship" as const, fields: [], label: "Ship it" as const, icon: "rocket" as const, description: "" as const }]).length === 1);
        fields = { ...(fields ?? {}), 'isLastStep': _fields['isLastStep'] } as Record<string, EventPayloadValue | undefined>;
        _fields['primaryActionLabel'] = ((([{ description: "" as const, key: "team" as const, label: "Meet the team" as const, icon: "users" as const, fields: [] }, { description: "" as const, fields: [], key: "build" as const, label: "Build your first system" as const, icon: "hammer" as const }, { fields: [], icon: "rocket" as const, description: "" as const, key: "ship" as const, label: "Ship it" as const }]).length === 1) ? "Submit" : "Next");
        fields = { ...(fields ?? {}), 'primaryActionLabel': _fields['primaryActionLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['primaryActionIcon'] = ((([{ fields: [], key: "team" as const, label: "Meet the team" as const, icon: "users" as const, description: "" as const }, { fields: [], description: "" as const, label: "Build your first system" as const, icon: "hammer" as const, key: "build" as const }, { key: "ship" as const, description: "" as const, fields: [], icon: "rocket" as const, label: "Ship it" as const }]).length === 1) ? "check-circle" : "chevron-right");
        fields = { ...(fields ?? {}), 'primaryActionIcon': _fields['primaryActionIcon'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'running';
    } else if (fromState === 'loading' && event === 'WizardLoadFailed') {
      // loading → error (on WizardLoadFailed)
        _fields['errorMessage'] = payloadObj?.error;
        fields = { ...(fields ?? {}), 'errorMessage': _fields['errorMessage'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'error';
    } else if (fromState === 'loading' && event === 'WizardSaved') {
      // loading → loading (on WizardSaved)
      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'WizardSaveFailed') {
      // loading → error (on WizardSaveFailed)
        _fields['errorMessage'] = payloadObj?.error;
        fields = { ...(fields ?? {}), 'errorMessage': _fields['errorMessage'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'error';
    } else if (fromState === 'running' && event === 'INIT') {
      // running → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'running' && event === 'ADVANCE' && (!fields?.isLastStep)) {
      // running → running (on ADVANCE)
        _fields['currentStepIndex'] = (+(fields?.currentStepIndex ?? 0) + 1);
        fields = { ...(fields ?? {}), 'currentStepIndex': _fields['currentStepIndex'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentStepLabel'] = getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ fields: [], description: "" as const, icon: "users" as const, key: "team" as const, label: "Meet the team" as const }, { fields: [], description: "" as const, key: "build" as const, icon: "hammer" as const, label: "Build your first system" as const }, { label: "Ship it" as const, fields: [], description: "" as const, key: "ship" as const, icon: "rocket" as const }], Number(fields?.currentStepIndex ?? 0)), "label");
        fields = { ...(fields ?? {}), 'currentStepLabel': _fields['currentStepLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentStepDescription'] = (getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ label: "Meet the team" as const, fields: [], description: "" as const, key: "team" as const, icon: "users" as const }, { description: "" as const, label: "Build your first system" as const, icon: "hammer" as const, key: "build" as const, fields: [] }, { description: "" as const, key: "ship" as const, fields: [], label: "Ship it" as const, icon: "rocket" as const }], Number(fields?.currentStepIndex ?? 0)), "description") ?? "");
        fields = { ...(fields ?? {}), 'currentStepDescription': _fields['currentStepDescription'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentStepIcon'] = (getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ icon: "users" as const, fields: [], description: "" as const, key: "team" as const, label: "Meet the team" as const }, { key: "build" as const, icon: "hammer" as const, label: "Build your first system" as const, fields: [], description: "" as const }, { icon: "rocket" as const, description: "" as const, label: "Ship it" as const, fields: [], key: "ship" as const }], Number(fields?.currentStepIndex ?? 0)), "icon") ?? "circle");
        fields = { ...(fields ?? {}), 'currentStepIcon': _fields['currentStepIcon'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentFields'] = (getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ key: "team" as const, icon: "users" as const, label: "Meet the team" as const, fields: [], description: "" as const }, { description: "" as const, label: "Build your first system" as const, fields: [], key: "build" as const, icon: "hammer" as const }, { description: "" as const, icon: "rocket" as const, key: "ship" as const, fields: [], label: "Ship it" as const }], Number(fields?.currentStepIndex ?? 0)), "fields") ?? []);
        fields = { ...(fields ?? {}), 'currentFields': _fields['currentFields'] } as Record<string, EventPayloadValue | undefined>;
        _fields['isFirstStep'] = (fields?.currentStepIndex === 0);
        fields = { ...(fields ?? {}), 'isFirstStep': _fields['isFirstStep'] } as Record<string, EventPayloadValue | undefined>;
        _fields['isLastStep'] = (fields?.currentStepIndex === (+(fields?.totalSteps ?? 0) - 1));
        fields = { ...(fields ?? {}), 'isLastStep': _fields['isLastStep'] } as Record<string, EventPayloadValue | undefined>;
        _fields['primaryActionLabel'] = ((fields?.currentStepIndex === (+(fields?.totalSteps ?? 0) - 1)) ? "Submit" : "Next");
        fields = { ...(fields ?? {}), 'primaryActionLabel': _fields['primaryActionLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['primaryActionIcon'] = ((fields?.currentStepIndex === (+(fields?.totalSteps ?? 0) - 1)) ? "check-circle" : "chevron-right");
        fields = { ...(fields ?? {}), 'primaryActionIcon': _fields['primaryActionIcon'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'running';
    } else if (fromState === 'running' && event === 'ADVANCE' && (fields?.isLastStep)) {
      // running → completed (on ADVANCE)
        _fields['completionMessage'] = "All steps completed. Your submission has been recorded.";
        fields = { ...(fields ?? {}), 'completionMessage': _fields['completionMessage'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'completed';
    } else if (fromState === 'running' && event === 'RETREAT') {
      // running → running (on RETREAT)
        _fields['currentStepIndex'] = (+(fields?.currentStepIndex ?? 0) - 1);
        fields = { ...(fields ?? {}), 'currentStepIndex': _fields['currentStepIndex'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentStepLabel'] = getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ fields: [], key: "team" as const, label: "Meet the team" as const, icon: "users" as const, description: "" as const }, { label: "Build your first system" as const, icon: "hammer" as const, description: "" as const, fields: [], key: "build" as const }, { description: "" as const, label: "Ship it" as const, fields: [], key: "ship" as const, icon: "rocket" as const }], Number(fields?.currentStepIndex ?? 0)), "label");
        fields = { ...(fields ?? {}), 'currentStepLabel': _fields['currentStepLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentStepDescription'] = (getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ fields: [], key: "team" as const, description: "" as const, icon: "users" as const, label: "Meet the team" as const }, { key: "build" as const, icon: "hammer" as const, description: "" as const, fields: [], label: "Build your first system" as const }, { fields: [], description: "" as const, key: "ship" as const, icon: "rocket" as const, label: "Ship it" as const }], Number(fields?.currentStepIndex ?? 0)), "description") ?? "");
        fields = { ...(fields ?? {}), 'currentStepDescription': _fields['currentStepDescription'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentStepIcon'] = (getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ label: "Meet the team" as const, key: "team" as const, description: "" as const, fields: [], icon: "users" as const }, { icon: "hammer" as const, fields: [], description: "" as const, key: "build" as const, label: "Build your first system" as const }, { description: "" as const, label: "Ship it" as const, fields: [], icon: "rocket" as const, key: "ship" as const }], Number(fields?.currentStepIndex ?? 0)), "icon") ?? "circle");
        fields = { ...(fields ?? {}), 'currentStepIcon': _fields['currentStepIcon'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentFields'] = (getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ key: "team" as const, fields: [], description: "" as const, icon: "users" as const, label: "Meet the team" as const }, { key: "build" as const, icon: "hammer" as const, label: "Build your first system" as const, description: "" as const, fields: [] }, { icon: "rocket" as const, label: "Ship it" as const, fields: [], key: "ship" as const, description: "" as const }], Number(fields?.currentStepIndex ?? 0)), "fields") ?? []);
        fields = { ...(fields ?? {}), 'currentFields': _fields['currentFields'] } as Record<string, EventPayloadValue | undefined>;
        _fields['isFirstStep'] = (fields?.currentStepIndex === 0);
        fields = { ...(fields ?? {}), 'isFirstStep': _fields['isFirstStep'] } as Record<string, EventPayloadValue | undefined>;
        _fields['isLastStep'] = (fields?.currentStepIndex === (+(fields?.totalSteps ?? 0) - 1));
        fields = { ...(fields ?? {}), 'isLastStep': _fields['isLastStep'] } as Record<string, EventPayloadValue | undefined>;
        _fields['primaryActionLabel'] = ((fields?.currentStepIndex === (+(fields?.totalSteps ?? 0) - 1)) ? "Submit" : "Next");
        fields = { ...(fields ?? {}), 'primaryActionLabel': _fields['primaryActionLabel'] } as Record<string, EventPayloadValue | undefined>;
        _fields['primaryActionIcon'] = ((fields?.currentStepIndex === (+(fields?.totalSteps ?? 0) - 1)) ? "check-circle" : "chevron-right");
        fields = { ...(fields ?? {}), 'primaryActionIcon': _fields['primaryActionIcon'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'running';
    } else if (fromState === 'running' && event === 'CANCEL') {
      // running → cancelled (on CANCEL)
        _fields['cancelReason'] = payloadObj?.reason;
        fields = { ...(fields ?? {}), 'cancelReason': _fields['cancelReason'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'cancelled';
    } else if (fromState === 'completed' && event === 'INIT') {
      // completed → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'completed' && event === 'WizardSaved') {
      // completed → completed (on WizardSaved)
      _newState = 'completed';
    } else if (fromState === 'completed' && event === 'WizardSaveFailed') {
      // completed → completed (on WizardSaveFailed)
      _newState = 'completed';
    } else if (fromState === 'completed' && event === 'RESTART') {
      // completed → loading (on RESTART)
        _fields['currentStepIndex'] = 0;
        fields = { ...(fields ?? {}), 'currentStepIndex': _fields['currentStepIndex'] } as Record<string, EventPayloadValue | undefined>;
        _fields['completionMessage'] = "";
        fields = { ...(fields ?? {}), 'completionMessage': _fields['completionMessage'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'cancelled' && event === 'INIT') {
      // cancelled → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'cancelled' && event === 'RESTART') {
      // cancelled → loading (on RESTART)
        _fields['currentStepIndex'] = 0;
        fields = { ...(fields ?? {}), 'currentStepIndex': _fields['currentStepIndex'] } as Record<string, EventPayloadValue | undefined>;
        _fields['cancelReason'] = "";
        fields = { ...(fields ?? {}), 'cancelReason': _fields['cancelReason'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'error' && event === 'INIT') {
      // error → loading (on INIT)
      _newState = 'loading';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: JoinENDemoWeekViewSlot[]; clear: JoinENDemoWeekViewSlot[] } => {
    switch (fromState) {
      case 'cancelled':
        switch (event) {
          case 'INIT': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          case 'RESTART': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'completed':
        switch (event) {
          case 'INIT': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          case 'RESTART': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          case 'WizardSaveFailed': return { write: ['toast'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          case 'WizardSaved': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'error':
        switch (event) {
          case 'INIT': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'loading':
        switch (event) {
          case 'INIT': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          case 'WizardLoadFailed': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          case 'WizardLoaded': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          case 'WizardSaveFailed': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          case 'WizardSaved': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'running':
        switch (event) {
          case 'ADVANCE': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          case 'CANCEL': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          case 'INIT': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          case 'RETREAT': return { write: ['main'] as JoinENDemoWeekViewSlot[], clear: [] as JoinENDemoWeekViewSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'JoinENDemoWeekStep': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as JoinENDemoWeekViewEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    if (__LEG_EVENTS.has(event)) {
    // Server leg: ordered by the pump, never blocking the drain.
    commandPump.enqueue(async () => {
      try {
        const response = await sendEvent(event, payload, prevState);
        if (response.success) {
          // Agreement — merge any fetch/persist results the local commit
          // couldn't supply (same reducer action, deferred leg).
          if (response.data && Object.keys(response.data).length > 0) {
            dispatchReducer({
              type: 'EVENT_SUCCESS',
              event: event as JoinENDemoWeekViewEvent,
              newState: machineStateRef.current,
              data: response.data,
              renderBearing: false,
            });
          }
        } else {
          // Rejection or state disagreement — roll back to the
          // pre-dispatch snapshot; the server stays authoritative.
          dispatchReducer({
            type: 'EVENT_SUCCESS',
            event: event as JoinENDemoWeekViewEvent,
            newState: prevState,
            data: {},
            fields: _snapshot,
            renderBearing: false,
          });
          machineStateRef.current = prevState;
          fieldsRef.current = { ...fieldsRef.current, ..._snapshot } as EntityRow;
          dispatchReducer({ type: 'EVENT_ERROR', error: response.error ?? `server state mismatch: expected ${_optimistic.newState}, got ${response.states?.['JoinENDemoWeekView'] ?? '(unchanged)'}` });
        }
      } catch (err) {
        // Pump isolates the rejection; surface it without rolling
        // back — local state may simply be ahead of a reconnecting server.
        dispatchReducer({ type: 'EVENT_ERROR', error: err instanceof Error ? err.message : 'Network error' });
      }
    });
    }
    return { success: true, transitioned: true, states: { 'JoinENDemoWeekView': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof JoinENDemoWeekViewEventPayloadMap]: (payload?: JoinENDemoWeekViewEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'WizardLoaded': (payload?: JoinENDemoWeekViewEventPayloadMap['WizardLoaded']) => dispatch('WizardLoaded', payload),
    'WizardLoadFailed': (payload?: JoinENDemoWeekViewEventPayloadMap['WizardLoadFailed']) => dispatch('WizardLoadFailed', payload),
    'WizardSaved': (payload?: JoinENDemoWeekViewEventPayloadMap['WizardSaved']) => dispatch('WizardSaved', payload),
    'WizardSaveFailed': (payload?: JoinENDemoWeekViewEventPayloadMap['WizardSaveFailed']) => dispatch('WizardSaveFailed', payload),
    'ADVANCE': (payload?: JoinENDemoWeekViewEventPayloadMap['ADVANCE']) => dispatch('ADVANCE', payload),
    'RETREAT': () => dispatch('RETREAT'),
    'CANCEL': (payload?: JoinENDemoWeekViewEventPayloadMap['CANCEL']) => dispatch('CANCEL', payload),
    'RESTART': () => dispatch('RESTART'),
  };

  useUIEvents(enqueueEvent, 'JoinEN.JoinENDemoWeekView', ['INIT', 'WizardLoaded', 'WizardLoadFailed', 'WizardSaved', 'WizardSaveFailed', 'ADVANCE', 'RETREAT', 'CANCEL', 'RESTART'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('JoinENDemoWeekView', (): TraitStateSnapshot => ({
      traitName: 'JoinENDemoWeekView',
      currentState: internalStateRef.current.machineState,
      states: ['loading', 'running', 'completed', 'cancelled', 'error'],
      events: ['INIT', 'WizardLoaded', 'WizardLoadFailed', 'WizardSaved', 'WizardSaveFailed', 'ADVANCE', 'RETREAT', 'CANCEL', 'RESTART'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'TOUR_STEP' (from JoinENDemoWeekTour) → trigger 'ADVANCE'
    const unsub_tour_step_from_joinendemoweektour_to_advance = eventBus.on('UI:JoinEN.JoinENDemoWeekTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "TO_BUILD"))) return;
      if (!claimDelivery(event.source, 'JoinEN.JoinENDemoWeekView', 'ADVANCE')) return;
      enqueueEvent('ADVANCE', { data: {  } } as EventPayload);
    });
    // Listen for 'TOUR_STEP' (from JoinENDemoWeekTour) → trigger 'ADVANCE'
    const unsub_tour_step_from_joinendemoweektour_to_advance_2 = eventBus.on('UI:JoinEN.JoinENDemoWeekTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "TO_SHIP"))) return;
      if (!claimDelivery(event.source, 'JoinEN.JoinENDemoWeekView', 'ADVANCE')) return;
      enqueueEvent('ADVANCE', { data: {  } } as EventPayload);
    });
    // Listen for 'TOUR_STEP' (from JoinENDemoWeekTour) → trigger 'ADVANCE'
    const unsub_tour_step_from_joinendemoweektour_to_advance_3 = eventBus.on('UI:JoinEN.JoinENDemoWeekTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "FINISH_WEEK"))) return;
      if (!claimDelivery(event.source, 'JoinEN.JoinENDemoWeekView', 'ADVANCE')) return;
      enqueueEvent('ADVANCE', { data: {  } } as EventPayload);
    });
    // Listen for 'TOUR_RESET' (from JoinENDemoWeekTour) → trigger 'RESTART'
    const unsub_tour_reset_from_joinendemoweektour_to_restart = eventBus.on('UI:JoinEN.JoinENDemoWeekTour.TOUR_RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'JoinEN.JoinENDemoWeekView', 'RESTART')) return;
      enqueueEvent('RESTART', event.payload);
    });
    return () => {
      if (typeof unsub_tour_step_from_joinendemoweektour_to_advance === 'function') unsub_tour_step_from_joinendemoweektour_to_advance();
      if (typeof unsub_tour_step_from_joinendemoweektour_to_advance_2 === 'function') unsub_tour_step_from_joinendemoweektour_to_advance_2();
      if (typeof unsub_tour_step_from_joinendemoweektour_to_advance_3 === 'function') unsub_tour_step_from_joinendemoweektour_to_advance_3();
      if (typeof unsub_tour_reset_from_joinendemoweektour_to_restart === 'function') unsub_tour_reset_from_joinendemoweektour_to_restart();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  mergedConfig.backLabel = mergedConfig.backLabel ?? (__t("std-almadar-demo-week:btn.back"));
  mergedConfig.cancelLabel = mergedConfig.cancelLabel ?? (__t("std-almadar-demo-week:btn.cancel"));
  mergedConfig.cancelledText = mergedConfig.cancelledText ?? (__t("std-almadar-demo-week:text.cancelled"));
  mergedConfig.cancelledTitle = mergedConfig.cancelledTitle ?? (__t("std-almadar-demo-week:title.cancelled"));
  mergedConfig.completedText = mergedConfig.completedText ?? (__t("std-almadar-demo-week:text.completed"));
  mergedConfig.completedTitle = mergedConfig.completedTitle ?? (__t("std-almadar-demo-week:title.completed"));
  mergedConfig.nextLabel = mergedConfig.nextLabel ?? (__t("std-almadar-demo-week:btn.next"));
  mergedConfig.restartLabel = mergedConfig.restartLabel ?? (__t("std-almadar-demo-week:btn.restart"));
  mergedConfig.stepDescriptions = mergedConfig.stepDescriptions ?? ([__t("std-almadar-demo-week:week.s1.desc"), __t("std-almadar-demo-week:week.s2.desc"), __t("std-almadar-demo-week:week.s3.desc")]);
  mergedConfig.stepLabels = mergedConfig.stepLabels ?? ([__t("std-almadar-demo-week:week.s1.label"), __t("std-almadar-demo-week:week.s2.label"), __t("std-almadar-demo-week:week.s3.label")]);
  mergedConfig.submitLabel = mergedConfig.submitLabel ?? (__t("std-almadar-demo-week:btn.submit"));
  mergedConfig.title = mergedConfig.title ?? (__t("std-almadar-demo-week:week.title"));
  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
